import { useState } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Avatar, Button, tokens } from '@hotjar/hotsauce';
import { useTranslation } from 'react-i18next';

import { getUserAvatarUrl } from '@hotjar/insights-common/lib/getUserAvatarUrl';

import type { UserId } from '@hotjar/user';
import type { Thread, Reply } from '../domains/model';
import { Content } from './Content';
import { MoreButton } from './MoreButton';
import { useScrollIntoView } from '../hooks/useScrollIntoView';

const StyledMoreButton = styled(MoreButton)`
  margin-top: -${tokens.spacer.x0};
`;

interface CommentContentProps {
  className?: string;
  onClose?: () => void;
  onDelete?: () => void;
  onShowMoreMenu?: () => void;
  comment: Thread | Reply;
  currentUserId: UserId;
  renderCreator: (
    onSave: () => void,
    comment: Thread | Reply,
    additionalButtons: React.ReactNode,
  ) => React.ReactNode;
}

const CommentContainer = styled.div`
  display: grid;
  grid-template-columns: 32px auto min-content;
  grid-column-gap: ${tokens.spacer.x1};
  padding: ${tokens.spacer.x1};
  // NOTE: it's to make focus outline for the more button visible from the right side
  padding-right: ${tokens.spacer.x0};

  // NOTE: this is to make text-overflow of Content to work; strange but required by grid layout
  overflow: hidden;
`;

export const CommentContent = ({
  className,
  comment,
  onClose,
  onDelete,
  onShowMoreMenu,
  currentUserId,
  renderCreator,
}: CommentContentProps): JSX.Element => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const { id, content, author, createdEpochTime } = comment;
  const isOwnComment = currentUserId === author.id;

  const ref = useScrollIntoView<HTMLDivElement>(id);

  return (
    <CommentContainer ref={ref} className={className}>
      <Avatar size="l" src={getUserAvatarUrl(author)} userName={author.name} className="avatar" />
      {!isEditing ? (
        <Content
          value={content}
          authorName={author.name}
          created={createdEpochTime}
          className="comment-content"
        />
      ) : (
        renderCreator(
          () => setIsEditing(false),
          comment,
          <Button
            css={css`
              margin-right: ${tokens.spacer.x1};
            `}
            kind="secondary"
            size="s"
            onClick={() => {
              setIsEditing(false);
              if (onClose) {
                onClose();
              }
            }}
          >
            {t('comment:create.cancel')}
          </Button>,
        )
      )}
      {!isEditing ? (
        <StyledMoreButton
          onEdit={isOwnComment ? () => setIsEditing(true) : undefined}
          onDelete={isOwnComment ? onDelete : undefined}
          onShowMoreMenu={onShowMoreMenu ?? undefined}
        />
      ) : null}
    </CommentContainer>
  );
};
